import { useCallback, useEffect, useState, type JSX } from 'react';
import '@xyflow/react/dist/style.css';
import { useI18n, type Lang } from './i18n';
import { useStore } from './state/store';
import { getCatalog, getGraph, getMe, getSchematic, listProjects, logout } from './api/client';
import { clearToken } from './auth/token';
import { ProjectPicker } from './components/ProjectPicker';
import { Toasts } from './components/Toasts';
import { PlantView } from './views/PlantView';
import { SignalView } from './views/SignalView';
import { MonitorView } from './views/MonitorView';
import { TrainingView } from './views/TrainingView';
import { DataView } from './views/DataView';
import { DashboardView } from './views/DashboardView';
import { HistoryView } from './views/HistoryView';
import { AuditView } from './views/AuditView';
import { SettingsView } from './views/SettingsView';
import { RegistrationsView } from './views/RegistrationsView';
import { SiteView } from './views/SiteView';
import type { UserInfo } from './api/types';

// Ansichten der IDE, obere Tab-Leiste wechselt zwischen ihnen. Der Standort (Issue #491) steht
// vorn: wo die Anlage steht, bevor es darum geht, was dort steht. Er erscheint nur, wenn das
// Projekt eine site-Sektion trägt — Referenz-Anlagen ohne realen Ort zeigen dort einen Hinweis. Training bettet JupyterLab
// same-origin ein (Issue #25), nach Monitor eingereiht. Die Daten-Ansicht (CSV-Eingänge +
// Spalten-Zuordnung) steht nach Training und vor der Anlagenüberwachung. Historie und Audit
// (Issue #82) hängen additiv an: Änderungshistorie mit Diff/Rollback und der fälschungssichere
// Audit-Trail. Die Einstellungen (Benutzer-/Rollenverwaltung, Issue #86) sind nur für Admins
// sichtbar (Rolle aus getMe()).
type ViewId =
  | 'site'
  | 'plant'
  | 'graph'
  | 'monitor'
  | 'training'
  | 'data'
  | 'dashboard'
  | 'history'
  | 'audit'
  | 'registrations'
  | 'settings';
const BASE_VIEWS: ViewId[] = [
  'site',
  'plant',
  'graph',
  'monitor',
  'training',
  'data',
  'dashboard',
  'history',
  'audit',
];
// Nur für Admins: die Freigabe-Warteschlange (Registrierungen, Issue #187/#191) und die
// Benutzer-/Rollenverwaltung (Issue #86).
const ADMIN_VIEWS: ViewId[] = ['registrations', 'settings'];

function LangSwitch(): JSX.Element {
  const { lang, setLang, t } = useI18n();
  const other: Lang = lang === 'de' ? 'en' : 'de';
  return (
    <button
      type="button"
      aria-label={t('header.language')}
      onClick={() => setLang(other)}
      className="rounded border border-bf-border px-3 py-1 text-sm text-bf-white hover:border-bf-gold"
    >
      {other.toUpperCase()}
    </button>
  );
}

// Nutzeranzeige + Abmelden/Benutzer-wechseln (Issue #197). Zeigt den angemeldeten Nutzer (aus
// /me) und einen Knopf, der serverseitig abmeldet (/auth/logout widerruft das Token im Store),
// den Token lokal verwirft und über clearToken den Anmelde-Screen anstößt. „Benutzer wechseln" ist
// bewusst dasselbe: abmelden und neu anmelden (der neue Login stellt ohnehin ein frisches Token
// aus). Der lokale Logout läuft auch dann, wenn der Server-Aufruf scheitert — der Nutzer kommt so
// nie in einem halb-angemeldeten Zustand fest.
function UserMenu({ me }: { me: UserInfo | null }): JSX.Element | null {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const name = me.displayName?.trim() || me.identity;

  const onLogout = async () => {
    setBusy(true);
    try {
      await logout();
    } catch {
      /* z. B. statisches Token ohne Store-Nutzer: der lokale Logout reicht */
    } finally {
      // Immer lokal aufräumen und den Anmelde-Screen zeigen (auch bei Server-Fehler).
      clearToken();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="hidden max-w-40 truncate text-sm text-bf-muted md:inline" title={me.identity}>
        {t('user.signedInAs', { name })}
      </span>
      <button
        type="button"
        onClick={onLogout}
        disabled={busy}
        data-testid="logout"
        className="rounded border border-bf-border px-3 py-1 text-sm text-bf-white transition-colors hover:border-bf-gold disabled:opacity-60"
      >
        {t('user.logout')}
      </button>
    </div>
  );
}

// Statusleiste am unteren Rand: aktives Projekt, Revision und die jüngste Meldung.
function StatusBar(): JSX.Element {
  const { t } = useI18n();
  const { activeProject, catalog, graph, toasts } = useStore();
  const last = toasts[toasts.length - 1];
  return (
    <footer className="flex flex-wrap items-center justify-between gap-x-3 border-t border-bf-border bg-bf-navy px-4 py-1.5 text-xs text-bf-muted">
      <span>
        {activeProject
          ? t('status.project', { name: activeProject.name })
          : t('status.noProject')}
        {catalog ? ` · ${t('status.catalog', { count: catalog.variables.length })}` : ''}
        {graph ? ` · ${t('status.graph', { count: graph.nodes.length })}` : ''}
      </span>
      {last && (
        <span className={last.kind === 'error' ? 'text-red-300' : 'text-bf-muted'}>
          {last.message}
        </span>
      )}
    </footer>
  );
}

export function App(): JSX.Element {
  const { t } = useI18n();
  const [active, setActive] = useState<ViewId>('plant');
  const [me, setMe] = useState<UserInfo | null>(null);
  const {
    activeProjectId,
    reloadNonce,
    setProjects,
    setCatalog,
    setGraph,
    setSchematic,
    reportError,
  } = useStore();

  // Der Freigabe- und der Einstellungen-Tab erscheinen nur, wenn der angemeldete Nutzer die
  // Admin-Rolle trägt. (Die Einstellungen-Ansicht degradiert für Nicht-Admins zusätzlich sauber
  // auf das eigene Profil.) Die harte Durchsetzung bleibt beim Backend (401/403).
  const isAdmin = (me?.roles ?? []).includes('admin');
  const VIEWS: ViewId[] = isAdmin ? [...BASE_VIEWS, ...ADMIN_VIEWS] : BASE_VIEWS;

  // Den eigenen Principal einmal beim Start laden — Rolle (Admin-Tabs) und Nutzeranzeige. Schlägt
  // der Aufruf fehl (z. B. statisches Token ohne Store-Nutzer), bleiben die Admin-Tabs aus und die
  // Nutzeranzeige leer; das ist der sichere Default.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((info) => !cancelled && setMe(info))
      .catch(() => {
        /* kein /me (z. B. statisches Token) → keine Admin-Tabs, still */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Projektliste beim Start laden.
  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((res) => !cancelled && setProjects(res.projects))
      .catch((err) => !cancelled && reportError(err, t('project.listFailed')));
    return () => {
      cancelled = true;
    };
  }, [setProjects, reportError, t]);

  // Katalog + Graph + Anlagenschema des aktiven Projekts nachladen (bei Projektwechsel).
  const loadProjectData = useCallback(
    async (projectId: string) => {
      try {
        const [cat, gr, sch] = await Promise.all([
          getCatalog(projectId),
          getGraph(projectId),
          getSchematic(projectId),
        ]);
        setCatalog(cat);
        setGraph(gr);
        setSchematic(sch);
      } catch (err) {
        reportError(err, t('project.loadFailed'));
      }
    },
    [setCatalog, setGraph, setSchematic, reportError, t],
  );

  // reloadNonce zieht ein Neuladen nach sich (z. B. nach einem Revert aus der Historie), damit die
  // übrigen Views (Katalog/Graph/Schema) den zurückgerollten Stand zeigen.
  useEffect(() => {
    if (activeProjectId) {
      void loadProjectData(activeProjectId);
    } else {
      setCatalog(null);
      setGraph(null);
      setSchematic(null);
    }
  }, [activeProjectId, reloadNonce, loadProjectData, setCatalog, setGraph, setSchematic]);

  return (
    <div className="flex h-screen flex-col bg-bf-navy text-bf-white">
      <header className="relative flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-bf-border px-4 py-3 md:px-6">
        <div className="flex items-baseline gap-3">
          <span className="text-lg font-bold tracking-tight text-bf-white">blackfortflow</span>
          <span className="hidden text-sm text-bf-muted lg:inline">{t('app.tagline')}</span>
        </div>
        {/* Auf dem Handy passt die Kopf-Zeile nicht nebeneinander — umbrechen statt rechts
            abschneiden (Issue #479). */}
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 md:gap-3">
          <ProjectPicker />
          <LangSwitch />
          <UserMenu me={me} />
        </div>
      </header>

      <nav
        className="flex shrink-0 gap-1 overflow-x-auto border-b border-bf-border px-4"
        aria-label={t('app.title')}
      >
        {VIEWS.map((view) => {
          const isActive = view === active;
          return (
            <button
              key={view}
              type="button"
              data-testid={`nav-${view}`}
              onClick={() => setActive(view)}
              aria-current={isActive ? 'page' : undefined}
              className={
                'shrink-0 whitespace-nowrap px-4 py-2 text-sm transition-colors ' +
                (isActive
                  ? 'border-b-2 border-bf-gold text-bf-white'
                  : 'border-b-2 border-transparent text-bf-muted hover:text-bf-white')
              }
            >
              {t(`nav.${view}`)}
            </button>
          );
        })}
      </nav>

      <main className="min-h-0 flex-1 overflow-hidden">
        {active === 'site' && <SiteView />}
        {active === 'plant' && <PlantView />}
        {active === 'graph' && <SignalView />}
        {active === 'monitor' && <MonitorView />}
        {active === 'training' && <TrainingView />}
        {active === 'data' && <DataView />}
        {active === 'dashboard' && <DashboardView />}
        {active === 'history' && <HistoryView />}
        {active === 'audit' && <AuditView />}
        {active === 'registrations' && isAdmin && <RegistrationsView />}
        {active === 'settings' && <SettingsView />}
      </main>

      <StatusBar />
      <Toasts />
    </div>
  );
}
